import { PRODUCTS } from './payments'
import { fetchKB } from './support'
import { fetchMarketplaceProducts, fetchVendorProducts } from './marketplace'

// Static site routes surfaced in global search.
export const SITE_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Products', to: '/products' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Build (R&D)', to: '/build' },
  { label: 'White-Label', to: '/whitelabel' },
  { label: 'Enterprise Deploy', to: '/deploy' },
  { label: 'Blog', to: '/blog' },
  { label: 'Changelog', to: '/changelog' },
  { label: 'Integrations', to: '/integrations' },
  { label: 'Company', to: '/company' },
  { label: 'Marketplace', to: '/marketplace' },
  { label: 'Help & Support', to: '/support' },
  { label: 'Notices & Privacy', to: '/privacy-policy' },
  { label: 'Terms of Service', to: '/terms-of-service' },
]

// Normalize a string for matching (lowercase, strip diacritics).
function norm(value) {
  return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function scoreMatch(sourceText, query) {
  if (!sourceText) return -1
  const s = norm(sourceText)
  const q = norm(query)
  if (!q) return -1
  if (s === q) return 100
  if (s.startsWith(q)) return 80
  const idx = s.indexOf(q)
  if (idx >= 0) return 60 - Math.min(idx, 20)
  // Token-level match (e.g. "squamed minime" matches when querying "minime").
  const tokens = s.split(/\s+/)
  if (tokens.some((t) => t.startsWith(q))) return 45
  if (tokens.some((t) => t.includes(q))) return 30
  return -1
}

// Build a flat result model from all sources. Cheap, synchronous where possible.
export async function buildSearchIndex() {
  const [kb, mkt, vendor] = await Promise.all([
    fetchKB().then((r) => r.articles || []).catch(() => []),
    fetchMarketplaceProducts().catch(() => []),
    fetchVendorProducts().catch(() => []),
  ])

  const results = []

  // Site pages
  for (const link of SITE_LINKS) {
    results.push({ group: 'Pages', type: 'page', title: link.label, href: link.to, keywords: link.label })
  }

  // Products + plans
  for (const p of PRODUCTS) {
    const planNames = (p.plans || []).map((pl) => pl.name).join(' ')
    results.push({
      group: 'Products',
      type: 'product',
      title: p.name,
      subtitle: p.tagline,
      href: `/products/${p.id}`,
      keywords: `${p.name} ${p.tagline} ${p.description || ''} ${planNames}`,
    })
  }

  // Blog / knowledge base articles
  for (const a of kb) {
    results.push({
      group: 'Help & Blog',
      type: 'article',
      title: a.title,
      subtitle: a.summary,
      href: `/blog/${a.slug}`,
      keywords: `${a.title} ${a.summary || ''} ${(a.tags || []).join(' ')} ${a.body || ''}`,
    })
  }

  // Marketplace add-ons & services
  for (const m of mkt) {
    results.push({
      group: 'Marketplace',
      type: 'marketplace',
      title: m.name,
      subtitle: m.short_desc,
      href: `/marketplace/${m.slug}`,
      keywords: `${m.name} ${m.short_desc || ''} ${(m.details || []).join(' ')}`,
    })
  }

  // Vendor partner apps
  for (const v of vendor) {
    results.push({
      group: 'Marketplace',
      type: 'marketplace',
      title: v.name,
      subtitle: v.short_desc || v.vendor?.business_name,
      href: `/marketplace/${v.slug}`,
      keywords: `${v.name} ${v.short_desc || ''} ${v.vendor?.business_name || ''}`,
    })
  }

  return results
}

// Rank filtered results for a query.
export function searchResults(index, query) {
  const q = String(query || '').trim()
  if (!q) return []
  const scored = []
  for (const item of index) {
    const s = scoreMatch(item.keywords, q)
    if (s < 0) continue
    scored.push({ ...item, score: s })
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, 24)
}